import { Logo } from "@/components/Logo";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mic, Drum, Guitar, Piano, GraduationCap, Sparkles, CheckCircle2, Film, Play } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { PlayAlongNote } from "@/components/PlayAlongPromo";
import { Button } from "@/components/ui/button";
import { INSTRUMENTS, INSTRUMENT_ART } from "@/data/curriculum";
import { useAuth } from "@/context/AuthContext";
import { GospelCard } from "@/components/GospelCourse";
import { PianoVideos } from "@/components/PianoVideos";
import { usePopularCourse, PopularBadge } from "@/components/PopularBadge";

const INST_ICON = { singers: Mic, drummers: Drum, guitarists: Guitar, pianists: Piano };
const COURSE_ICON = { foundations: GraduationCap, mastery: Sparkles };
const COURSES_HERO =
  "https://images.unsplash.com/photo-1618609377864-68609b857e90?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Courses() {
  const { progress } = useAuth();
  const popular = usePopularCourse();
  const completed = new Set(progress?.completed_lessons || []);
  const [params] = useSearchParams();
  const initInst = INSTRUMENTS.some((i) => i.id === params.get("instrument")) ? params.get("instrument") : INSTRUMENTS[0].id;
  const [active, setActive] = useState(initInst);
  const [pianoView, setPianoView] = useState(params.get("view") === "videos" ? "videos" : "courses");
  const showVideos = () => { setPianoView("videos"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const instrument = INSTRUMENTS.find((i) => i.id === active);

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 sm:px-8 py-10 lg:py-16">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl border border-border"
        >
          <img src={COURSES_HERO} alt="Music studio mixing desk" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/10" />
          <div className="relative min-h-56 p-6 pt-28 sm:min-h-72 sm:p-8 sm:pt-32">
            <div className="mb-3" data-testid="courses-logo"><Logo height={56} /></div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Pick your instrument</p>
            <h1 className="mt-2 font-serif text-4xl tracking-tight sm:text-5xl">Courses for every musician</h1>
            <p className="mt-3 max-w-2xl text-muted-foreground" data-testid="courses-subtext">
              Choose your instrument, then choose between the Beginners course or the Intermediate course. Just 10 lessons in each level.
            </p>
            <p className="mt-2 max-w-2xl text-sm text-foreground/90" data-testid="courses-genres">Gospel, Funk, Jazz, Blues, Soul, R&B, Classical – whatever the genre, The JT Hub can teach you the theory needed for it.</p>
          </div>
        </motion.div>

        {/* Instrument tabs */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="instrument-tabs">
          {INSTRUMENTS.map((inst) => {
            const Icon = INST_ICON[inst.id] || Mic;
            const on = active === inst.id;
            return (
              <button
                key={inst.id}
                onClick={() => { setActive(inst.id); setPianoView("courses"); }}
                data-testid={`instrument-tab-${inst.id}`}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${
                  on
                    ? "bg-primary text-foreground shadow-md shadow-amber-900/20"
                    : "border border-border bg-card text-muted-foreground hover:border-primary hover:text-foreground"
                }`}
              >
                <Icon size={17} /> {inst.label}
              </button>
            );
          })}
        </div>

        {(
          <div className="mt-6 inline-flex rounded-full border border-border bg-card p-1" data-testid="piano-subtabs">
            {[["courses", "Courses"], ["videos", "Jason in action"]].map(([k, l]) => (
              <button key={k} onClick={() => setPianoView(k)} data-testid={`piano-subtab-${k}`}
                className={`rounded-full px-5 py-2 text-sm transition-colors ${pianoView === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        )}
        {pianoView === "videos" ? <PianoVideos instrumentId={active} /> : (
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8"
        >
          <Link to={`/instruments/${instrument.id}`} className="group relative mb-6 block overflow-hidden rounded-3xl border border-border" data-testid={`instrument-banner-${instrument.id}`}>
            {instrument.id === "pianists" ? (
              <>
                <div className="h-40 w-full bg-gradient-to-r from-background to-card sm:h-52" />
                <img src={INSTRUMENT_ART[instrument.id]} alt="Jason Thompson playing a grand piano" data-testid="pianists-banner-photo"
                  className="absolute right-0 top-0 h-full w-1/2 object-cover object-[center_47%] transition-transform duration-500 group-hover:scale-105 sm:w-[24rem]" />
                <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-r from-background to-transparent sm:w-[24rem]" style={{ maskImage: "linear-gradient(to right, #000, transparent 40%)" }} />
              </>
            ) : (
              <>
                <img src={INSTRUMENT_ART[instrument.id]} alt={instrument.label} className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-52" />
                <div className="absolute inset-0 bg-gradient-to-r from-background via-background/75 to-background/20" />
              </>
            )}
            <div className="absolute inset-0 flex flex-col justify-center p-6 sm:p-8">
              <span className="font-serif text-3xl text-foreground sm:text-4xl">{instrument.label}</span>
              <span className="mt-1 text-sm text-muted-foreground">{instrument.tagline}</span>
              <span className="mt-3 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-primary">
                View instrument page <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>

          <div className="grid gap-6 lg:grid-cols-2">
            {instrument.courses.map((course, i) => {
              const Icon = COURSE_ICON[course.id] || GraduationCap;
              const doneCount = course.lessons.filter((l) => completed.has(l.id)).length;
              const pct = Math.round((doneCount / course.lessons.length) * 100);
              return (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.08 }}
                  className="flex flex-col rounded-3xl border border-border bg-card p-8"
                  data-testid={`course-card-${instrument.id}-${course.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-primary">
                      <Icon size={22} />
                    </span>
                    <span className="rounded-full bg-primary/10 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-primary">
                      {course.tagline}
                    </span>
                  </div>
                  <h2 className="mt-6 font-serif text-2xl leading-tight">{course.title}</h2>
                  {popular?.course_key === `${instrument.id}:${course.id}` && <div className="mt-2"><PopularBadge testId={`course-popular-${instrument.id}-${course.id}`} /></div>}
                  <p className="mt-3 flex-1 text-sm text-muted-foreground">{course.blurb}</p>
                  <PlayAlongNote testId={`courses-playalong-note-${instrument.id}-${course.id}`} />

                  <div className="mt-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{doneCount}/{course.lessons.length} lessons</span>
                      <span className="font-medium text-primary">{pct}%</span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <Link to={`/courses/${instrument.id}/${course.id}`} className="flex-1" data-testid={`open-course-${instrument.id}-${course.id}`}>
                      <Button className="group w-full rounded-full bg-primary py-6 text-base text-foreground hover:bg-primary/90">
                        {doneCount > 0 ? "Continue course" : "Start course"}
                        <ArrowRight size={17} className="ml-2 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </Link>
                    {pct === 100 && (
                      <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={14} /> Done
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
          <GospelCard instrument={instrument} />
          {(
            <button onClick={showVideos} data-testid="piano-see-me-in-action-button"
              className="mt-8 flex w-full items-center justify-between gap-4 rounded-3xl border border-primary/40 bg-primary/10 p-6 text-left transition-colors hover:bg-primary/15">
              <span>
                <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Film size={14} /> Jason in action</span>
                <span className="mt-1 block font-serif text-2xl">Watch Jason's performance videos</span>
              </span>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"><Play size={20} className="ml-0.5" /></span>
            </button>
          )}
        </motion.div>
        )}
      </div>
    </div>
  );
}
